import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { Modal } from "app/components/Modal";
import { DropdownIcon } from "app/icons";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  valueForOnlyNumericInputTextFields,
  valueForOnlyStringInputTextFields,
} from "utils/helpers";

import {
  CUSTOMER_FORM_FIELDS,
  REQUIRED_CUSTOMER_FIELDS,
} from "../../data";
import {
  selectCustomerForm,
  selectCustomerSaving,
  selectIsAddModalOpen,
} from "../../redux/selector";
import { customersActions } from "../../redux/slice";
import {
  fieldConfig,
  CustomerForm,
  CustomerSelectOption,
} from "../../types";
import { useStyles } from "./styles";

const CUSTOMERS_MODAL = "CUSTOMERS.MODAL";

const AddCustomerModal = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const dispatch = useDispatch();
  const open = useSelector(selectIsAddModalOpen);
  const form = useSelector(selectCustomerForm);
  const saving = useSelector(selectCustomerSaving);

  const onClose = () => {
    if (saving) return;
    dispatch(customersActions.closeAddModal());
  };

  const setField = (name: keyof CustomerForm, value: string) => {
    const nextValue =
      name === "contactPhone"
        ? valueForOnlyNumericInputTextFields(value)
        : name === "customerName" || name === "contactPerson"
          ? valueForOnlyStringInputTextFields(value)
          : value;

    dispatch(customersActions.setFormField({ name, value: nextValue }));
  };

  const placeholder = (field: fieldConfig) => {
    if (field.placeholder) {
      return t(`${CUSTOMERS_MODAL}.PLACEHOLDERS.${field.placeholder}`);
    }

    return t(
      `${CUSTOMERS_MODAL}.PLACEHOLDERS.${field.type === "select" ? "SELECT" : "ENTER"}_${field.label}`
    );
  };

  const optionLabel = (option: CustomerSelectOption) =>
    option.labelKey ? t(option.labelKey) : option.label || option.value;

  const isValid = REQUIRED_CUSTOMER_FIELDS.every((key) =>
    Boolean(form?.[key]?.trim())
  );

  return (
    <Modal open={open} onClose={onClose}>
      <Box className={styles.modal}>
        <Box className={styles.header}>
          <Typography className={styles.title}>{t(`${CUSTOMERS_MODAL}.ADD_TITLE`)}</Typography>
          <IconButton
            className={styles.closeButton}
            onClick={onClose}
            aria-label={t("BUTTONS.CANCEL")}
            disabled={saving}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className={styles.body}>
          {CUSTOMER_FORM_FIELDS.map((field) => {
            const { key, label, type, options = [] } = field;
            

            return (
              <Box key={key} className={styles.field}>
                <Typography className={styles.label}>
                  {t(`${CUSTOMERS_MODAL}.FIELDS.${label}`)}
                </Typography>
                <TextField
                  className={styles.input}
                  fullWidth
                  select={type === "select"}
                  variant="outlined"
                  placeholder={placeholder(field)}
                  value={form[key]}
                  inputMode={key === "contactPhone" ? "numeric" : "text"}
                  disabled={saving}
                  InputProps={{ notched: false }}
                  SelectProps={
                    type === "select"
                      ? {
                          displayEmpty: true,
                          IconComponent: DropdownIcon,
                          renderValue: (value) => {
                            const selected = options.find(
                              (option) => option.value === value
                            );
                            if (selected) {
                              return optionLabel(selected);
                            }

                            return (
                              <span className={styles.placeholder}>
                                {placeholder(field)}
                              </span>
                            );
                          },
                        }
                      : {}
                  }
                  onChange={(event) => setField((key) as keyof CustomerForm, event.target.value)}
                >
                  {type === "select"
                    ? [
                        <MenuItem key="empty" value="" disabled>
                          {placeholder(field)}
                        </MenuItem>,
                        ...options.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {optionLabel(option)}
                          </MenuItem>
                        )),
                      ]
                    : []}
                </TextField>
              </Box>
            );
          })}
        </Box>

        <Box className={styles.footer}>
          <Button
            className={styles.cancelButton}
            variant="outlined"
            onClick={onClose}
            disabled={saving}
          >
            {t("BUTTONS.CANCEL")}
          </Button>
          <Button
            className={styles.addButton}
            variant="contained"
            disableElevation
            disabled={!isValid || saving}
            onClick={() => dispatch(customersActions.addCustomer())}
          >
            {t("BUTTONS.ADD")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddCustomerModal;
